import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { adminGet, adminPatch, adminPost, adminDelete } from "../../lib/admin-api";
import {
  ArrowLeft,
  Building2,
  MapPin,
  Users,
  TrendingUp,
  Calendar,
  Compass,
  ShieldCheck,
  XCircle,
  Trash2,
  Activity,
  LogIn,
} from "lucide-react";

interface TenantDetail {
  id: string;
  name: string;
  slug: string;
  contactEmail: string;
  contactPhone: string | null;
  plan: string;
  stripeSubscriptionId: string | null;
  planStatus: string | null;
  isActive: boolean;
  retentionDays: number;
  createdAt: string;
  updatedAt: string;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  utmTerm: string | null;
  utmContent: string | null;
  gclid: string | null;
  gaClientId: string | null;
  referrer: string | null;
  landingPath: string | null;
  heardAboutUs: string | null;
  verificationStatus: string;
  uen: string | null;
  verifiedAt: string | null;
  reviewedBy: string | null;
  rejectionReason: string | null;
  checkInCount: number;
  checkInsThisMonth: number;
  locations: {
    id: string;
    name: string;
    address: string | null;
    isActive: boolean;
    createdAt: string;
  }[];
  users: {
    id: string;
    email: string;
    name: string | null;
    role: string;
    createdAt: string;
  }[];
}

type ActivityRange = "30d" | "90d" | "all";

// The plan selector lists every tier from the canonical pricing catalog
// (GET /api/pricing), in TIER_ORDER, so adding a tier there surfaces it here.
interface PlanOption {
  key: string;
  name: string;
}

interface TenantActivity {
  timeZone: "Asia/Singapore";
  range: { key: ActivityRange; startDate: string; endDate: string; endInclusive: true };
  selectedLocationId: string | null;
  locations: { id: string; name: string }[];
  checkIns: { total: number; distinctVisitors: number; repeatCheckIns: number };
  dashboardEngagement: {
    totalSessions: number;
    activeAdmins: number;
    activeDays: number;
    lastDashboardAccess: string | null;
  };
  daily: { date: string; totalCheckIns: number; distinctVisitors: number; dashboardSessions: number }[];
  feed: ({
    type: "checkin_activity";
    date: string;
    count: number;
    location: { id: string; name: string };
    label: string;
  } | {
    type: "dashboard_sessions";
    date: string;
    count: number;
    label: string;
  })[];
}

export function TenantDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState<TenantDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [activity, setActivity] = useState<TenantActivity | null>(null);
  const [activityLoading, setActivityLoading] = useState(true);
  const [activityError, setActivityError] = useState<string | null>(null);
  const [activityRange, setActivityRange] = useState<ActivityRange>("30d");
  const [locationId, setLocationId] = useState("");
  const [planOptions, setPlanOptions] = useState<PlanOption[]>([]);
  const [planError, setPlanError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    adminGet<TenantDetail>(`/tenants/${id}`).then((res) => {
      if (res.success && res.data) setTenant(res.data);
      setLoading(false);
    });
  }, [id]);

  useEffect(() => {
    let active = true;
    fetch("/api/pricing")
      .then((r) => r.json())
      .then((body) => {
        if (!active || !body?.data?.tiers) return;
        setPlanOptions(
          (body.data.tiers as PlanOption[]).map((t) => ({ key: t.key, name: t.name })),
        );
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!id) return;
    let active = true;
    setActivityLoading(true);
    setActivityError(null);
    const params = new URLSearchParams({ range: activityRange });
    if (locationId) params.set("locationId", locationId);
    adminGet<TenantActivity>(`/tenants/${id}/activity?${params}`).then((res) => {
      if (!active) return;
      if (res.success && res.data) setActivity(res.data);
      else setActivityError(res.error?.message || "Activity data could not be loaded.");
      setActivityLoading(false);
    }).catch(() => {
      if (!active) return;
      setActivityError("Activity data could not be loaded.");
      setActivityLoading(false);
    });
    return () => { active = false; };
  }, [id, activityRange, locationId]);

  async function toggleActive() {
    if (!tenant) return;
    const res = await adminPatch<TenantDetail>(`/tenants/${tenant.id}`, { isActive: !tenant.isActive });
    if (res.success && res.data) {
      setTenant({ ...tenant, isActive: res.data.isActive as unknown as boolean });
    }
  }

  async function changePlan(plan: string) {
    if (!tenant) return;
    setPlanError(null);
    const res = await adminPatch<TenantDetail>(`/tenants/${tenant.id}`, { plan });
    if (res.success && res.data) {
      setTenant(res.data);
    } else {
      setPlanError(res.error?.message || "The plan could not be changed.");
    }
  }

  const [actioning, setActioning] = useState(false);

  async function approveVerification() {
    if (!tenant) return;
    setActioning(true);
    const res = await adminPost<TenantDetail>(`/tenants/${tenant.id}/verification/approve`);
    if (res.success && res.data) {
      setTenant({ ...tenant, verificationStatus: "APPROVED", verifiedAt: res.data.verifiedAt, rejectionReason: null });
    }
    setActioning(false);
  }

  async function rejectVerification() {
    if (!tenant) return;
    const reason = window.prompt("Reason for rejecting this business (shown to the customer):");
    if (!reason || !reason.trim()) return;
    setActioning(true);
    const res = await adminPost<TenantDetail>(`/tenants/${tenant.id}/verification/reject`, { reason: reason.trim() });
    if (res.success && res.data) {
      setTenant({ ...tenant, verificationStatus: "REJECTED", verifiedAt: null, rejectionReason: reason.trim() });
    }
    setActioning(false);
  }

  async function deleteTenant() {
    if (!tenant) return;
    const confirmed = window.confirm(
      `Permanently delete "${tenant.name}"?\n\n` +
        "This removes the tenant and ALL its users, locations and check-in records. " +
        "This cannot be undone.",
    );
    if (!confirmed) return;
    setActioning(true);
    const res = await adminDelete(`/tenants/${tenant.id}`);
    setActioning(false);
    if (res.success) {
      navigate("/admin/tenants");
    } else {
      window.alert(res.error?.message || "Failed to delete tenant.");
    }
  }

  if (loading) return <div className="p-4 sm:p-6 text-stone-500">Loading...</div>;
  if (!tenant) return <div className="p-4 sm:p-6 text-stone-500">Tenant not found.</div>;

  const hasLiveSubscription = Boolean(
    tenant.stripeSubscriptionId &&
      (!tenant.planStatus || !["canceled", "incomplete_expired"].includes(tenant.planStatus)),
  );

  return (
    <div className="p-4 sm:p-6">
      <button
        onClick={() => navigate("/admin/tenants")}
        className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-900 mb-4 transition-colors"
      >
        <ArrowLeft size={16} />
        Back to tenants
      </button>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-brand-50 rounded-xl flex items-center justify-center">
            <Building2 size={24} className="text-brand-500" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-semibold text-stone-900 font-display">{tenant.name}</h1>
              <VerificationBadge status={tenant.verificationStatus} />
            </div>
            <p className="text-sm text-stone-500">{tenant.contactEmail}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-start gap-2">
          <div>
            <select
              value={tenant.plan}
              onChange={(e) => changePlan(e.target.value)}
              disabled={hasLiveSubscription}
              title={hasLiveSubscription ? "This plan is managed through Stripe billing." : undefined}
              className="px-3 py-1.5 rounded-lg border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:bg-stone-100 disabled:text-stone-500 disabled:cursor-not-allowed"
            >
              {(planOptions.some((p) => p.key === tenant.plan)
                ? planOptions
                : [...planOptions, { key: tenant.plan, name: tenant.plan }]
              ).map((p) => (
                <option key={p.key} value={p.key}>
                  {p.name}
                </option>
              ))}
            </select>
            {hasLiveSubscription && (
              <p className="mt-1 max-w-72 text-xs text-amber-700">
                Plan changes are unavailable because Stripe manages this subscription. Use the tenant billing flow.
              </p>
            )}
            {planError && (
              <p className="mt-1 max-w-72 text-xs text-red-600" role="alert">{planError}</p>
            )}
          </div>
          <button
            onClick={toggleActive}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              tenant.isActive
                ? "bg-red-50 text-red-700 hover:bg-red-100"
                : "bg-green-50 text-green-700 hover:bg-green-100"
            }`}
          >
            {tenant.isActive ? "Deactivate" : "Activate"}
          </button>
          <button
            onClick={deleteTenant}
            disabled={actioning}
            title="Permanently delete this tenant and all its data"
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
          >
            <Trash2 size={15} />
            Delete
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <MiniStat icon={<TrendingUp size={18} />} label="Total check-ins" value={tenant.checkInCount} />
        <MiniStat icon={<Calendar size={18} />} label="This month" value={tenant.checkInsThisMonth} />
        <MiniStat icon={<MapPin size={18} />} label="Locations" value={tenant.locations.length} />
        <MiniStat icon={<Users size={18} />} label="Users" value={tenant.users.length} />
      </div>

      <ActivityPanel
        activity={activity}
        loading={activityLoading}
        error={activityError}
        range={activityRange}
        locationId={locationId}
        onRangeChange={setActivityRange}
        onLocationChange={setLocationId}
      />

      <div className="bg-white rounded-xl border border-stone-200 p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <ShieldCheck size={16} className="text-stone-400" />
            <h2 className="text-sm font-medium text-stone-700">Business verification (KYB)</h2>
            <VerificationBadge status={tenant.verificationStatus} />
          </div>
          {tenant.verificationStatus !== "APPROVED" && (
            <div className="flex items-center gap-2">
              <button
                onClick={approveVerification}
                disabled={actioning}
                className="px-4 py-1.5 rounded-lg text-sm font-medium bg-green-50 text-green-700 hover:bg-green-100 disabled:opacity-50"
              >
                Approve
              </button>
              <button
                onClick={rejectVerification}
                disabled={actioning}
                className="px-4 py-1.5 rounded-lg text-sm font-medium bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50"
              >
                Reject
              </button>
            </div>
          )}
        </div>

        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm mb-4">
          <Row label="UEN (provided at signup)" value={tenant.uen || "—"} />
          <Row label="Phone" value={tenant.contactPhone || "—"} />
          <Row label="How did you hear about us" value={tenant.heardAboutUs || "—"} />
          <Row label="Reviewed by" value={tenant.reviewedBy || "—"} />
          <Row label="Verified at" value={tenant.verifiedAt ? new Date(tenant.verifiedAt).toLocaleString() : "—"} />
        </dl>

        {tenant.rejectionReason && (
          <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-100 px-3 py-2 rounded-lg mb-4">
            <XCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{tenant.rejectionReason}</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-stone-200 p-5">
          <h2 className="text-sm font-medium text-stone-700 mb-4">Details</h2>
          <dl className="space-y-3 text-sm">
            <Row label="Slug" value={tenant.slug} />
            <Row label="Phone" value={tenant.contactPhone || "—"} />
            <Row label="Plan" value={tenant.plan} />
            <Row label="Status" value={tenant.isActive ? "Active" : "Inactive"} />
            <Row label="Retention" value={tenant.retentionDays ? `${tenant.retentionDays} days` : "Disabled"} />
            <Row label="Registered" value={new Date(tenant.createdAt).toLocaleDateString()} />
          </dl>
        </div>

        <div className="bg-white rounded-xl border border-stone-200 p-5">
          <h2 className="text-sm font-medium text-stone-700 mb-4">Users</h2>
          {tenant.users.length === 0 ? (
            <p className="text-sm text-stone-400">No users.</p>
          ) : (
            <div className="space-y-2">
              {tenant.users.map((u) => (
                <div key={u.id} className="flex items-center justify-between py-2 border-b border-stone-50 last:border-0">
                  <div>
                    <p className="text-sm font-medium text-stone-900">{u.name || u.email}</p>
                    {u.name && <p className="text-xs text-stone-400">{u.email}</p>}
                  </div>
                  <span className="text-xs text-stone-500 bg-stone-100 px-2 py-0.5 rounded-full">{u.role}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-stone-200 p-5 lg:col-span-2">
          <div className="flex items-center gap-2 mb-4">
            <Compass size={16} className="text-stone-400" />
            <h2 className="text-sm font-medium text-stone-700">Signup attribution</h2>
            <span className="text-xs text-stone-500 bg-stone-100 px-2 py-0.5 rounded-full">
              {attributionSummary(tenant)}
            </span>
          </div>
          {hasAttribution(tenant) ? (
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
              <Row label="Source" value={tenant.utmSource || "—"} />
              <Row label="Medium" value={tenant.utmMedium || "—"} />
              <Row label="Campaign" value={tenant.utmCampaign || "—"} />
              <Row label="Term" value={tenant.utmTerm || "—"} />
              <Row label="Content" value={tenant.utmContent || "—"} />
              <Row label="Google Click ID" value={tenant.gclid ? "Yes (paid)" : "—"} />
              <Row label="GA client ID" value={tenant.gaClientId || "—"} />
              <Row label="Referrer" value={tenant.referrer || "—"} />
              <Row label="Landing page" value={tenant.landingPath || "—"} />
              <Row label="Said they heard via" value={tenant.heardAboutUs || "—"} />
            </dl>
          ) : (
            <p className="text-sm text-stone-400">
              No attribution captured — this account registered before tracking was added, or arrived directly.
            </p>
          )}
        </div>

        <div className="bg-white rounded-xl border border-stone-200 p-5 lg:col-span-2">
          <h2 className="text-sm font-medium text-stone-700 mb-4">Locations</h2>
          {tenant.locations.length === 0 ? (
            <p className="text-sm text-stone-400">No locations.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-stone-500 border-b border-stone-100">
                    <th className="pb-3 font-medium">Name</th>
                    <th className="pb-3 font-medium">Address</th>
                    <th className="pb-3 font-medium">Status</th>
                    <th className="pb-3 font-medium">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {tenant.locations.map((loc) => (
                    <tr key={loc.id} className="border-b border-stone-50">
                      <td className="py-3 text-stone-900 font-medium">{loc.name}</td>
                      <td className="py-3 text-stone-600">{loc.address || "—"}</td>
                      <td className="py-3">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                          loc.isActive ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"
                        }`}>
                          {loc.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="py-3 text-stone-500">{new Date(loc.createdAt).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ActivityPanel({
  activity,
  loading,
  error,
  range,
  locationId,
  onRangeChange,
  onLocationChange,
}: {
  activity: TenantActivity | null;
  loading: boolean;
  error: string | null;
  range: ActivityRange;
  locationId: string;
  onRangeChange: (range: ActivityRange) => void;
  onLocationChange: (locationId: string) => void;
}) {
  return (
    <section className="bg-white rounded-xl border border-stone-200 mb-6 overflow-hidden" aria-labelledby="activity-heading">
      <div className="p-5 border-b border-stone-100">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Activity size={17} className="text-brand-500" />
              <h2 id="activity-heading" className="text-base font-semibold text-stone-900 font-display">Tenant activity</h2>
            </div>
            <p className="mt-1 text-xs text-stone-500">Anonymous aggregates by Singapore calendar date. No visitor identity data is shown.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full lg:w-auto">
            <label className="block">
              <span className="block text-xs font-medium text-stone-600 mb-1">Period</span>
              <select
                value={range}
                onChange={(event) => onRangeChange(event.target.value as ActivityRange)}
                className="w-full lg:w-40 px-3 py-2 rounded-lg border border-stone-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
                <option value="all">All time</option>
              </select>
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-stone-600 mb-1">Location</span>
              <select
                value={locationId}
                onChange={(event) => onLocationChange(event.target.value)}
                className="w-full lg:w-52 px-3 py-2 rounded-lg border border-stone-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                <option value="">All locations</option>
                {activity?.locations.map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}
              </select>
            </label>
          </div>
        </div>
      </div>

      {loading && !activity ? <ActivitySkeleton /> : error ? (
        <div className="p-5 text-sm text-red-700 bg-red-50">{error}</div>
      ) : activity ? (
        <div className={loading ? "opacity-60 transition-opacity" : "transition-opacity"} aria-busy={loading}>
          <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-stone-100">
            <ActivityMetric label="Check-ins" value={activity.checkIns.total} />
            <ActivityMetric label="Distinct visitors" value={activity.checkIns.distinctVisitors} />
            <ActivityMetric label="Repeat check-ins" value={activity.checkIns.repeatCheckIns} detail="Check-ins beyond each visitor's first in this period; not repeat people." />
            <ActivityMetric label="Dashboard sessions" value={activity.dashboardEngagement.totalSessions} />
          </div>

          <div className="p-5 border-t border-stone-100">
            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between mb-4">
              <h3 className="text-sm font-medium text-stone-700">Daily check-in activity</h3>
              <p className="text-xs text-stone-500">{formatActivityDate(activity.range.startDate)} – {formatActivityDate(activity.range.endDate)} · SGT</p>
            </div>
            {activity.range.key === "all" ? (
              <div className="rounded-lg border border-stone-200 bg-stone-50 px-4 py-8 text-center">
                <p className="text-sm font-medium text-stone-700">Daily chart available for 30-day and 90-day periods</p>
                <p className="text-xs text-stone-500 mt-1">Choose a shorter period to compare daily check-ins and distinct visitors.</p>
              </div>
            ) : (
              <ActivityChart daily={activity.daily} />
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] border-t border-stone-100">
            <div className="p-5 lg:border-r border-stone-100">
              <div className="flex items-center gap-2 mb-4">
                <LogIn size={16} className="text-stone-400" />
                <h3 className="text-sm font-medium text-stone-700">Dashboard engagement</h3>
              </div>
              <dl className="space-y-3 text-sm">
                <Row label="Successful sessions" value={activity.dashboardEngagement.totalSessions.toLocaleString()} />
                <Row label="Active tenant admins" value={activity.dashboardEngagement.activeAdmins.toLocaleString()} />
                <Row label="Active days" value={activity.dashboardEngagement.activeDays.toLocaleString()} />
                <Row label="Last dashboard access" value={activity.dashboardEngagement.lastDashboardAccess ? new Date(activity.dashboardEngagement.lastDashboardAccess).toLocaleString("en-SG", { timeZone: "Asia/Singapore" }) : "—"} />
              </dl>
            </div>
            <div className="p-5 min-w-0">
              <h3 className="text-sm font-medium text-stone-700 mb-4">Anonymous activity feed</h3>
              {activity.feed.length === 0 ? (
                <p className="text-sm text-stone-400 py-4">No check-ins or dashboard sessions occurred in this period.</p>
              ) : (
                <div className="max-h-72 overflow-y-auto divide-y divide-stone-100">
                  {activity.feed.map((event, index) => (
                    <div key={`${event.type}-${event.date}-${index}`} className="flex items-start justify-between gap-4 py-3 first:pt-0">
                      <div className="min-w-0">
                        <p className="text-sm text-stone-800 truncate">{event.label}</p>
                        <p className="text-xs text-stone-400 mt-0.5">Aggregate activity only</p>
                      </div>
                      <time className="text-xs text-stone-500 shrink-0" dateTime={event.date}>{formatActivityDate(event.date)}</time>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function ActivityMetric({ label, value, detail }: { label: string; value: number; detail?: string }) {
  return (
    <div className="p-4 sm:p-5 min-w-0">
      <p className="text-2xl font-semibold text-stone-900 font-display tabular-nums">{value.toLocaleString()}</p>
      <p className="text-xs font-medium text-stone-600 mt-1">{label}</p>
      {detail && <p className="text-[11px] leading-4 text-stone-400 mt-1 max-w-56">{detail}</p>}
    </div>
  );
}

function ActivityChart({ daily }: { daily: TenantActivity["daily"] }) {
  const maximum = Math.max(1, ...daily.map((row) => row.totalCheckIns));
  return (
    <div>
      <div className="h-40 flex items-end gap-px sm:gap-1 border-b border-stone-200" role="img" aria-label="Daily total check-ins and distinct visitors">
        {daily.map((row) => (
          <div key={row.date} className="group relative flex-1 min-w-0 h-full flex items-end">
            <div className="w-full relative bg-brand-100 rounded-t-sm" style={{ height: `${Math.max(row.totalCheckIns ? 4 : 0, row.totalCheckIns / maximum * 100)}%` }}>
              <div className="absolute inset-x-0 bottom-0 bg-brand-500 rounded-t-sm" style={{ height: `${row.totalCheckIns ? row.distinctVisitors / row.totalCheckIns * 100 : 0}%` }} />
            </div>
            <div className="hidden group-hover:block absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-[1] whitespace-nowrap rounded-md bg-stone-900 px-2 py-1.5 text-[11px] text-white shadow-sm">
              {formatActivityDate(row.date)} · {row.totalCheckIns} check-ins · {row.distinctVisitors} visitors
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-4 mt-3 text-xs text-stone-500">
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-brand-100 border border-brand-200" />Total check-ins</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-brand-500" />Distinct visitors</span>
      </div>
    </div>
  );
}

function ActivitySkeleton() {
  return (
    <div className="animate-pulse" aria-label="Loading activity">
      <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-stone-100">
        {[0, 1, 2, 3].map((item) => <div key={item} className="p-5"><div className="h-7 w-16 bg-stone-100 rounded" /><div className="h-3 w-24 bg-stone-100 rounded mt-2" /></div>)}
      </div>
      <div className="m-5 h-40 bg-stone-50 rounded-lg" />
    </div>
  );
}

function formatActivityDate(date: string): string {
  return new Intl.DateTimeFormat("en-SG", { day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Singapore" }).format(new Date(`${date}T00:00:00+08:00`));
}

function hasAttribution(t: TenantDetail): boolean {
  return Boolean(
    t.utmSource || t.utmMedium || t.utmCampaign || t.utmTerm || t.utmContent ||
    t.gclid || t.gaClientId || t.referrer || t.landingPath || t.heardAboutUs,
  );
}

function attributionSummary(t: TenantDetail): string {
  if (t.gclid) return "Google Ads (paid)";
  if (t.utmSource) return `${t.utmSource}${t.utmMedium ? ` / ${t.utmMedium}` : ""}`;
  if (t.referrer) {
    let host = t.referrer;
    try {
      host = new URL(t.referrer).hostname.replace(/^www\./, "");
    } catch {
      /* keep raw */
    }
    if (/(^|\.)google\./.test(host)) return "Organic search (Google)";
    if (/(^|\.)bing\./.test(host)) return "Organic search (Bing)";
    if (/duckduckgo|yahoo|baidu|ecosia/.test(host)) return "Organic search";
    if (/chatgpt\.com|openai\.com/.test(host)) return "ChatGPT";
    if (/claude\.ai|perplexity\.ai|gemini\.google/.test(host)) return "AI assistant";
    if (/facebook|instagram|linkedin|t\.co|twitter|x\.com|tiktok/.test(host)) return `Social · ${host}`;
    return `Referral · ${host}`;
  }
  if (t.heardAboutUs) return `Self-reported · ${t.heardAboutUs}`;
  return "Direct / unknown";
}

function VerificationBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    APPROVED: "bg-green-50 text-green-700",
    PENDING: "bg-amber-50 text-amber-700",
    REJECTED: "bg-red-50 text-red-700",
  };
  const label: Record<string, string> = {
    APPROVED: "Verified",
    PENDING: "Pending verification",
    REJECTED: "Rejected",
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] ?? "bg-stone-100 text-stone-600"}`}>
      {label[status] ?? status}
    </span>
  );
}

function MiniStat({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <div className="bg-white rounded-xl border border-stone-200 p-4 flex items-center gap-3">
      <div className="w-9 h-9 bg-stone-50 rounded-lg flex items-center justify-center text-stone-400">
        {icon}
      </div>
      <div>
        <p className="text-xl font-semibold text-stone-900 font-display">{value.toLocaleString()}</p>
        <p className="text-xs text-stone-500">{label}</p>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-stone-500">{label}</dt>
      <dd className="text-stone-900 font-medium">{value}</dd>
    </div>
  );
}
